import { FC, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import { toast } from 'react-toastify'
import { Plus, Pencil, Trash2 } from 'lucide-react'
import { TOKENS, ConfirmDialog, PageCard } from '../../../components/design-system'
import { TRIGGER_LABELS, CHANNEL_LABELS, NotificationRule } from '../types'
import { createRule, deleteRule, listRules, updateRule } from '../api'
import { RuleEditor } from './RuleEditor'

type Props = {
  scope: 'global' | 'company'
  companyId?: number
}

export const RulesList: FC<Props> = ({ scope, companyId }) => {
  const qc = useQueryClient()
  const [editor, setEditor] = useState<{ mode: 'create' | 'edit'; rule: NotificationRule | null } | null>(null)
  const [confirmDelete, setConfirmDelete] = useState<NotificationRule | null>(null)

  const { data: rules = [], isLoading } = useQuery(
    ['notifications', 'rules', scope, companyId],
    () => listRules({ scope, company_id: scope === 'company' ? companyId : undefined }),
    { enabled: scope === 'global' || (scope === 'company' && !!companyId) },
  )

  const createMut = useMutation((body: any) => createRule(body), {
    onSuccess: () => {
      toast.success('Regra criada.')
      qc.invalidateQueries(['notifications', 'rules'])
      setEditor(null)
    },
    onError: (e: any) => { toast.error(`Erro ao criar: ${e?.response?.data?.message ?? e.message}`) },
  })

  const updateMut = useMutation(({ id, body }: { id: number; body: any }) => updateRule(id, body), {
    onSuccess: () => {
      toast.success('Regra atualizada.')
      qc.invalidateQueries(['notifications', 'rules'])
      setEditor(null)
    },
    onError: (e: any) => { toast.error(`Erro ao atualizar: ${e?.response?.data?.message ?? e.message}`) },
  })

  const deleteMut = useMutation((id: number) => deleteRule(id), {
    onSuccess: () => {
      toast.success('Regra excluída.')
      qc.invalidateQueries(['notifications', 'rules'])
      setConfirmDelete(null)
    },
    onError: (e: any) => { toast.error(`Erro ao excluir: ${e?.response?.data?.message ?? e.message}`) },
  })

  const handleSave = async (body: any) => {
    if (editor?.mode === 'create') {
      await createMut.mutateAsync(body)
    } else if (editor?.mode === 'edit' && editor.rule) {
      await updateMut.mutateAsync({ id: editor.rule.id, body })
    }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: TOKENS.spacing.md }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <div style={{ fontSize: 16, fontWeight: 700, color: TOKENS.color.text }}>
            {scope === 'global' ? 'Regras globais' : 'Regras da empresa'}
          </div>
          <div style={{ fontSize: 12, color: TOKENS.color.muted, marginTop: 2 }}>
            {scope === 'global'
              ? 'Aplicam a todas as empresas. Cada empresa pode sobrescrever criando regra própria.'
              : 'Sobrescrevem as regras globais para esta empresa.'}
          </div>
        </div>
        <button
          type='button'
          onClick={() => setEditor({ mode: 'create', rule: null })}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            background: TOKENS.color.action, color: '#fff',
            border: 'none', borderRadius: TOKENS.radius.card,
            padding: '8px 14px', fontSize: 13, fontWeight: 700,
            cursor: 'pointer',
          }}
        ><Plus size={14} /> Nova regra</button>
      </div>

      {isLoading && <div style={{ color: TOKENS.color.muted, fontSize: 12 }}>Carregando…</div>}
      {!isLoading && rules.length === 0 && (
        <PageCard>
          <div style={{ color: TOKENS.color.muted, fontSize: 13, textAlign: 'center', padding: '20px 0' }}>
            Nenhuma regra configurada ainda.
          </div>
        </PageCard>
      )}

      {rules.map((rule) => (
        <PageCard key={rule.id} style={{ padding: 0 }}>
          <div style={{ padding: TOKENS.spacing.md, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: TOKENS.spacing.md }}>
            <div style={{ flex: 1 }}>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: TOKENS.color.text }}>{rule.name}</div>
                {!rule.enabled && (
                  <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 6px', borderRadius: TOKENS.radius.chip, background: TOKENS.color.divider, color: TOKENS.color.muted }}>
                    DESATIVADA
                  </span>
                )}
              </div>
              {rule.description && (
                <div style={{ fontSize: 12, color: TOKENS.color.muted, marginTop: 4 }}>{rule.description}</div>
              )}
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 8 }}>
                {rule.triggers.map((t) => (
                  <span key={t.id} style={{
                    fontSize: 10, fontWeight: 600, padding: '3px 8px',
                    borderRadius: TOKENS.radius.chip, background: TOKENS.color.actionSoft, color: TOKENS.color.action,
                  }}>{TRIGGER_LABELS[t.trigger_type] ?? t.trigger_type}</span>
                ))}
                <span style={{ color: TOKENS.color.muted, fontSize: 10, alignSelf: 'center' }}>→</span>
                {rule.actions.map((a) => (
                  <span key={a.id} style={{
                    fontSize: 10, fontWeight: 600, padding: '3px 8px',
                    borderRadius: TOKENS.radius.chip, background: TOKENS.color.surface, color: TOKENS.color.muted,
                    border: `1px solid ${TOKENS.color.border}`,
                  }}>{CHANNEL_LABELS[a.channel as 'email' | 'whatsapp' | 'webhook'] ?? a.channel}</span>
                ))}
              </div>
            </div>
            <div style={{ display: 'flex', gap: 6 }}>
              <button type='button' title='Editar' onClick={() => setEditor({ mode: 'edit', rule })}
                style={{ background: 'transparent', border: 'none', color: TOKENS.color.muted, cursor: 'pointer', padding: 6 }}
              ><Pencil size={16} /></button>
              <button type='button' title='Excluir' onClick={() => setConfirmDelete(rule)}
                style={{ background: 'transparent', border: 'none', color: TOKENS.color.severity.high.text, cursor: 'pointer', padding: 6 }}
              ><Trash2 size={16} /></button>
            </div>
          </div>
        </PageCard>
      ))}

      {/* Editor modal */}
      {editor && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(17,24,39,0.45)', zIndex: 1100,
          display: 'flex', alignItems: 'center', justifyContent: 'center', padding: TOKENS.spacing.lg,
        }}>
          <div style={{
            width: '100%', maxWidth: 720, maxHeight: '90vh', overflowY: 'auto',
            background: TOKENS.color.card, borderRadius: TOKENS.radius.card,
            boxShadow: '0 20px 50px rgba(17,24,39,0.25)',
          }}>
            <div style={{ padding: '14px 20px', borderBottom: `1px solid ${TOKENS.color.border}` }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: TOKENS.color.text }}>
                {editor.mode === 'create' ? 'Nova regra' : 'Editar regra'}
              </div>
            </div>
            <div style={{ padding: 20 }}>
              <RuleEditor
                initial={editor.rule}
                defaultScope={scope}
                defaultCompanyId={companyId}
                onSave={handleSave}
                onCancel={() => setEditor(null)}
                saving={createMut.isLoading || updateMut.isLoading}
              />
            </div>
          </div>
        </div>
      )}

      <ConfirmDialog
        open={!!confirmDelete}
        title='Excluir regra?'
        description={confirmDelete ? `A regra "${confirmDelete.name}" será removida permanentemente.` : ''}
        confirmLabel='Excluir'
        tone='danger'
        loading={deleteMut.isLoading}
        onConfirm={() => { if (confirmDelete) deleteMut.mutate(confirmDelete.id) }}
        onCancel={() => setConfirmDelete(null)}
      />
    </div>
  )
}
